/**
 * Pending format state for collapsed cursor formatting.
 *
 * When the cursor is collapsed and the user toggles a format (e.g. Bold),
 * we store the pending state here instead of applying it immediately.
 * The next typed character will be wrapped in the pending formatting.
 *
 * Moving the cursor or making a selection clears the pending state.
 */

export interface PendingFormatState {
	bold?: boolean;
	italic?: boolean;
	underline?: boolean;
	strikethrough?: boolean;
	subscript?: boolean;
	superscript?: boolean;
	fontSize?: string;
	color?: string;
}

type PendingKey = keyof PendingFormatState;

let pendingState: PendingFormatState = {};

export const setPending = (key: PendingKey, value: boolean | string): void => {
	(pendingState as Record<string, boolean | string>)[key] = value;
};

export const getPending = (): PendingFormatState => pendingState;

export const hasPending = (): boolean => Object.keys(pendingState).length > 0;

export const clearPending = (): void => {
	pendingState = {};
};

/**
 * Consume the pending state — returns current state and clears it.
 * Called when inserting text that should carry the pending formatting.
 */
export const consumePending = (): PendingFormatState => {
	const state = { ...pendingState };
	pendingState = {};
	return state;
};

/** Zero-Width Space character used as a formatting anchor */
export const ZWS = "\u200B";

/** Find a text node with the given content within a root node. */
const findTextNode = (root: Node, content: string): Node | null => {
	if (root.nodeType === Node.TEXT_NODE && root.textContent === content)
		return root;
	for (const child of Array.from(root.childNodes)) {
		const found = findTextNode(child, content);
		if (found) return found;
	}
	return null;
};

/**
 * Build an HTML string wrapping `content` in the appropriate formatting tags
 * based on the given pending state.
 *
 * SAFETY: `content` is interpolated directly into HTML via template literals.
 * Currently only called with the ZWS constant ("\u200B") which is safe.
 * Do NOT pass user-controlled input without HTML-escaping first.
 */
export const buildFormattedHtml = (
	state: PendingFormatState,
	content: string,
): string => {
	let html = content;

	if (state.bold) html = `<strong>${html}</strong>`;
	if (state.italic) html = `<em>${html}</em>`;
	if (state.underline) html = `<u>${html}</u>`;
	if (state.strikethrough) html = `<s>${html}</s>`;
	if (state.subscript) html = `<sub>${html}</sub>`;
	if (state.superscript) html = `<sup>${html}</sup>`;

	const styles: string[] = [];
	if (state.fontSize) styles.push(`font-size: ${state.fontSize}`);
	if (state.color) styles.push(`color: ${state.color}`);

	if (styles.length > 0) {
		html = `<span style="${styles.join("; ")}">${html}</span>`;
	}

	return html;
};

/**
 * Flush pending state by inserting a ZWS wrapped in formatting tags.
 * The cursor is placed inside the formatted wrapper so subsequent typing
 * inherits the formatting naturally.
 *
 * Returns true if pending state was flushed, false if nothing was pending.
 */
export const flushPendingState = (): boolean => {
	if (!hasPending()) return false;

	const state = consumePending();
	const html = buildFormattedHtml(state, ZWS);

	const sel = window.getSelection();
	if (!sel || sel.rangeCount === 0) return false;

	const range = sel.getRangeAt(0);
	if (!range.collapsed) return false;

	try {
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = html;

		const fragment = document.createDocumentFragment();
		while (tempDiv.firstChild) {
			fragment.appendChild(tempDiv.firstChild);
		}

		range.insertNode(fragment);

		// Position cursor after the ZWS (inside the formatted wrapper)
		const searchRoot =
			range.commonAncestorContainer.parentElement ??
			range.commonAncestorContainer;
		const zwsNode = findTextNode(searchRoot, ZWS);
		if (zwsNode) {
			const newRange = document.createRange();
			newRange.setStart(zwsNode, 1);
			newRange.collapse(true);
			sel.removeAllRanges();
			sel.addRange(newRange);
		}
	} catch (err) {
		console.warn("[Froala] Failed to flush pending format state:", err);
		return false;
	}

	return true;
};
